'use client'

import { getPlatformName } from '@/actions/platform'
import EventAttendeeStatusButton from '@/app/(public)/c/[communitySlug]/[championshipSlug]/[eventId]/components/event-attendee-status-button'
import EventCardImageTitle from '@/components/_cards/event/driver-calendar/event-card-image-title'
import ImageWithFallback from '@/components/common/image-with-fallback'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getChampionshipEventDriverStats } from '@/helpers/championship'
import { timezoneDateTime } from '@/helpers/datetime'
import { getUserProfilePictureUrl } from '@/helpers/user'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import ViewSessions from './view-sessions'

export default function CalendarEventCard({ event, session }) {
  const [raceStart, setRaceStart] = useState(null)

  const gameNiceName = event.championship.game?.nicename

  // TODO change to SWR request
  useEffect(() => {
    const raceStartTemp = timezoneDateTime({
      datetime: event.startDateTime,
      timezone: event.targetTimezone,
      format: false,
    })

    setRaceStart(raceStartTemp)
  }, [])

  const { attendees, gridPositionsLabel } = useMemo(
    () => getChampionshipEventDriverStats(event, event.championship),
    [event],
  )

  const platformDisplayName = useMemo(() => {
    return getPlatformName(event.championship.platforms)
  }, [event])

  return (
    <div className="relative">
      <div className="rounded-md border border-neutral-blue-700 bg-neutral-blue-800 p-[16px] font-inter">
        <div className="flex flex-col">
          <div className="mt-2 xl:flex">
            <EventCardImageTitle event={event} noBg={true} />
            <div className="mt-[24px] flex h-auto w-full flex-row-reverse justify-between gap-4 xl:mt-0 xl:w-[40%] xl:justify-normal desktop-lg:w-[50%]">
              <div className="flex h-auto w-[50%] rounded-sm border border-neutral-blue-700 px-[5px]">
                <div className="mx-auto flex flex-col self-center py-[24px]">
                  <div className="self-center text-center text-[18px] text-neutral-blue-500">
                    Start time
                  </div>
                  <div className="self-center text-[23px] text-white">
                    <p className="text-center font-bold">
                      {raceStart ? raceStart.format('HH:mm') : 'N/A'}
                    </p>
                  </div>
                  <div className="self-center text-neutral-blue-500">
                    <p className="text-center text-[18px]">
                      {raceStart && raceStart.format('z')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex h-auto w-[50%] rounded-sm border border-neutral-blue-700 px-[5px]">
                <div className="mx-auto flex flex-col self-center py-[24px]">
                  <div className="self-center text-center text-[18px] text-neutral-blue-500">
                    Start date
                  </div>
                  <div className="self-center text-center text-[23px] font-bold text-white">
                    {raceStart ? raceStart['$D'] : 'N/A'}
                  </div>
                  <div className="self-center text-center text-[18px] text-neutral-blue-500">
                    {raceStart && raceStart.format('MMMM')}{' '}
                    {raceStart && raceStart.format('YYYY')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ViewSessions event={event} />

          <div className="col-span-2 flex justify-between">
            <div className="flex align-middle">
              {attendees.length > 0 && (
                <div className="flex hidden items-center -space-x-2 self-center xl:inline-block">
                  {attendees
                    .sort(() => 0.5 - Math.random())
                    .slice(0, 3)
                    .map((member) => (
                      <div
                        key={`member-icon-${member.userId}`}
                        className="relative inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-none"
                      >
                        <div className="relative w-[32px]">
                          <ImageWithFallback
                            id={`member-image-${member.userId}`}
                            fallbackUrl="/img/default/header.webp"
                            alt="User Image"
                            src={getUserProfilePictureUrl(member.user, {
                              profile: 'driver',
                            })}
                            fill
                          />
                        </div>
                      </div>
                    ))}
                </div>
              )}
              <div className="ml-[10px] mr-[16px] hidden self-center text-[18px] text-neutral-blue-500 underline xl:inline-block">
                {attendees.length}/{gridPositionsLabel} Drivers Registered
              </div>
              <div className="hidden gap-4 self-center text-nowrap text-[22px] font-normal text-shades-0 desktop-sm:flex">
                {platformDisplayName !== 'Multi-Platform' ? (
                  platformDisplayName
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <span className="w-fit cursor-pointer">
                          Multi-Platform ({event.championship.platforms.length})
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="max-h-[300px] overflow-y-auto">
                          {event.championship.platforms.map((platform) => (
                            <div
                              key={`platform-${platform.platform.name}`}
                              className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors hover:bg-neutral-blue-700"
                            >
                              {platform.platform.nicename}
                            </div>
                          ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                <div className="font-weight-[400] self-center text-nowrap text-[22px] text-[#627C9A]">
                  {gameNiceName}
                </div>
              </div>
            </div>
            <div className="flex w-full flex-row-reverse gap-4 xl:w-auto xl:gap-0">
              <EventAttendeeStatusButton
                event={event}
                session={session}
                mutateEventDrivers={() => {}}
              />

              <div className="mr-0 flex w-full items-center rounded-sm border border-neutral-blue-500 px-4 py-3 xl:mr-[16px] xl:w-fit">
                <Link
                  href={`/c/${event.championship.community.slug}/${event.championship.slug}/${event.id}`}
                  className="mx-auto"
                >
                  <span className="self-center text-nowrap">View Event</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
